import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Eye, ListChecks, BadgeCheck } from "lucide-react";
import { Reveal, HeroLine, FadeIn } from "@/components/Reveal";
import { usePageTitle } from "@/hooks/use-page-title";

const HERO_IMG =
  "https://images.pexels.com/photos/16632950/pexels-photo-16632950.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Eyebrow = ({ children }) => (
  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">{children}</p>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  return (
    <section ref={ref} data-testid="home-hero" className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-aspira-gold/10 blur-3xl" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-16 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:pb-28 lg:pt-24">
        <div>
          <FadeIn>
            <Eyebrow>Vacant land, bought &amp; sold</Eyebrow>
          </FadeIn>
          <h1 className="mt-6 font-serif text-4xl font-medium leading-[1.08] tracking-tight text-aspira-ink sm:text-5xl lg:text-6xl">
            <HeroLine delay={0.15}>Your Land.</HeroLine>
            <HeroLine delay={0.3}>
              Your Next <em className="italic text-aspira-gold">Chapter.</em>
            </HeroLine>
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-aspira-body">
              Whether you're looking to sell vacant land or find the right
              property for your next investment, Aspira Property Group makes the
              process straightforward, transparent, and personal.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Link
                to="/sell"
                data-testid="hero-cta-sell"
                className="group inline-flex items-center gap-2 rounded-full bg-aspira-gold px-7 py-3.5 text-sm font-semibold text-white shadow-md transition-all duration-300 hover:bg-aspira-goldhover hover:shadow-xl"
              >
                Sell Your Land
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/buy"
                data-testid="hero-cta-buy"
                className="inline-flex items-center gap-2 rounded-full border border-aspira-ink/15 bg-white px-7 py-3.5 text-sm font-semibold text-aspira-ink transition-all duration-300 hover:border-aspira-gold hover:text-aspira-gold"
              >
                Explore Available Land
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-[0.16em] text-aspira-muted">
              <span>Straightforward process</span>
              <span className="text-aspira-gold">◆</span>
              <span>Clear communication</span>
              <span className="text-aspira-gold">◆</span>
              <span>For buyers &amp; sellers</span>
            </div>
          </FadeIn>
        </div>
        <FadeIn delay={0.35} className="relative">
          <div className="absolute -inset-3 rounded-[2rem] border border-aspira-gold/40" />
          <div className="relative overflow-hidden rounded-[1.75rem] shadow-2xl">
            <motion.img
              src={HERO_IMG}
              alt="Open landscape of vacant land at dusk"
              style={{ y: imgY }}
              className="h-[420px] w-full scale-110 object-cover lg:h-[520px]"
            />
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

const HowWeHelp = () => (
  <section data-testid="how-we-help" className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
    <Reveal>
      <Eyebrow>How we help</Eyebrow>
      <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium leading-tight tracking-tight text-aspira-ink sm:text-4xl">
        One team for both sides of a land deal.
      </h2>
    </Reveal>
    <div className="mt-12 grid gap-6 lg:grid-cols-2">
      <Reveal delay={0.1}>
        <div className="group h-full rounded-3xl border border-aspira-goldborder bg-white p-8 transition-all duration-500 hover:border-aspira-gold hover:shadow-2xl sm:p-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">Selling land</p>
          <h3 className="mt-4 font-serif text-2xl font-medium text-aspira-ink sm:text-3xl">
            A comfortable way to sell your property
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-aspira-body">
            Tell us about your land and we'll review the details, discuss your
            options, and provide an offer if the property fits our acquisition
            criteria. No pressure, no obligation.
          </p>
          <Link
            to="/sell"
            data-testid="help-cta-sell"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-aspira-gold px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-aspira-goldhover"
          >
            Learn about selling <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
      <Reveal delay={0.2}>
        <div className="group h-full rounded-3xl border border-aspira-goldborder bg-white p-8 transition-all duration-500 hover:border-aspira-gold hover:shadow-2xl sm:p-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-aspira-gold">Buying land</p>
          <h3 className="mt-4 font-serif text-2xl font-medium text-aspira-ink sm:text-3xl">
            Properties worth considering
          </h3>
          <p className="mt-4 text-sm leading-relaxed text-aspira-body">
            Browse available land from Aspira Property Group — recreational
            acreage, future homesites, and investment parcels, each presented
            with clear, honest details.
          </p>
          <Link
            to="/buy"
            data-testid="help-cta-buy"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-aspira-ink/15 px-6 py-3 text-sm font-semibold text-aspira-ink transition-all duration-300 hover:border-aspira-gold hover:text-aspira-gold"
          >
            Browse available land <ArrowRight size={15} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const PRINCIPLES = [
  [Eye, "Transparency", "Clear communication throughout the process — you'll always know where things stand."],
  [ListChecks, "Simplicity", "A straightforward experience with no unnecessary complications."],
  [BadgeCheck, "Professionalism", "Thoughtful evaluation and respectful service, on every property."],
];

const OurApproach = () => (
  <section data-testid="our-approach" className="bg-white py-20 lg:py-24">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Our approach</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight text-aspira-ink sm:text-4xl">
          Three principles, every deal.
        </h2>
      </Reveal>
      <div className="mt-12 grid gap-6 sm:grid-cols-3">
        {PRINCIPLES.map(([Icon, title, body], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div
              data-testid={`principle-${title.toLowerCase()}`}
              className="group h-full rounded-3xl border border-aspira-goldborder/60 bg-aspira-cream p-8 transition-all duration-300 hover:-translate-y-1 hover:border-aspira-gold hover:shadow-xl"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-aspira-goldlight text-aspira-gold transition-colors duration-300 group-hover:bg-aspira-gold group-hover:text-white">
                <Icon size={20} />
              </span>
              <h3 className="mt-5 font-serif text-xl font-medium text-aspira-ink">{title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-aspira-body">{body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCta = () => (
  <section data-testid="final-cta" className="relative overflow-hidden bg-aspira-dark py-20 lg:py-24">
    <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-aspira-gold/10 blur-3xl" />
    <div className="pointer-events-none absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-aspira-gold/10 blur-3xl" />
    <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
      <Reveal>
        <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight text-aspira-cream sm:text-4xl">
          Have a Property in Mind?
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-sm leading-relaxed text-aspira-cream/70 sm:text-base">
          Whether you're ready to sell or looking for your next land opportunity,
          start a conversation with Aspira Property Group.
        </p>
        <div className="mt-9 flex flex-wrap justify-center gap-4">
          <Link
            to="/sell"
            data-testid="final-cta-sell"
            className="inline-flex items-center gap-2 rounded-full bg-aspira-gold px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-aspira-goldhover"
          >
            Sell Your Land <ArrowRight size={15} />
          </Link>
          <Link
            to="/buy"
            data-testid="final-cta-buy"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-aspira-cream transition-all duration-300 hover:border-aspira-gold hover:text-aspira-gold"
          >
            Browse Properties
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

const Home = () => {
  usePageTitle("Aspira Property Group | Sell Vacant Land for Cash & Buy Land");
  return (
    <>
      <Hero />
      <HowWeHelp />
      <OurApproach />
      <FinalCta />
    </>
  );
};

export default Home;
